/* eslint-disable max-lines */
import { BehaviorSubject } from 'rxjs';

import { FeatureFlag } from '@models/feature-flag.model';

import { AuxIconName } from '@shared/components/icon/icon';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { Flags } from '@shared/services/launch-darkly.service';

export interface NavigationMenuItem {
  activeSvg?: AuxIconName;
  badge?: {
    amount: BehaviorSubject<number>;
    tooltip: string;
  };
  className?: string;
  divider?: boolean;
  flagKey?: keyof Flags;
  flagKeys?: (keyof Flags)[];
  flyOutMenu?: NavigationMenuItem[];
  hiddenFromNavigation?: boolean;
  name: string;
  primaryBadgeInfo?: string;
  rightSvg?: AuxIconName;
  singleRoute?: boolean;
  subRoutes: NavigationMenuItem[];
  svg?: AuxIconName;
  svgClasses?: string;
  url: string;
}

export class NavigationMenuCost {
  static readonly ROUTER_CONFIG: NavigationMenuItem[] = [
    {
      flagKey: 'tab_forecast_in_month',
      flagKeys: [
        'checklist_tab',
        'tab_in_month_adjustments',
        'month_quarter_close_tab',
        'tab_invoices',
        'prepaids',
        'tab_budget_cash_management',
        'tab_journal_entries',
      ],
      name: 'Period Close',
      subRoutes: [
        {
          flagKey: 'checklist_tab',
          name: 'Checklist',
          subRoutes: [],
          url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CHECKLIST}`,
        },
        {
          flagKeys: ['tab_invoices', 'prepaids'],
          flyOutMenu: [
            {
              flagKey: 'tab_invoices',
              name: 'Invoices',
              subRoutes: [],
              url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES}`,
            },
            {
              flagKey: 'prepaids',
              name: 'Prepaids',
              subRoutes: [],
              url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.PREPAIDS}`,
            },
          ],
          name: 'Invoices & Prepaids',
          subRoutes: [],
          url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}`,
        },
        {
          flagKey: 'tab_in_month_adjustments',
          name: 'Expense Detail',
          subRoutes: [],
          url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`,
        },
        {
          flagKey: 'month_quarter_close_tab',
          name: 'Close Summary',
          subRoutes: [],
          url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CLOSE_SUMMARY}`,
        },
        {
          flagKey: 'tab_budget_cash_management',
          name: 'Reconciliation',
          subRoutes: [],
          url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.RECONCILIATION}`,
        },
        {
          flagKey: 'tab_journal_entries',
          name: 'Journal Entries',
          subRoutes: [],
          url: `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.JOURNAL_ENTRIES}`,
        },
      ],
      svg: 'CalendarClock',
      url: `/${ROUTING_PATH.CLOSING.INDEX}`,
    },
    {
      flagKey: 'nav_budget',
      name: 'Budgets',
      subRoutes: [
        {
          flagKeys: ['tab_compare', 'tab_budget_budget_library'],
          flyOutMenu: [
            {
              name: 'Vendor Budgets',
              subRoutes: [],
              url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.VENDOR_BUDGET}`,
            },
            {
              flagKey: 'tab_budget_budget_library',
              name: 'Budget Library',
              subRoutes: [],
              url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.LIBRARY}`,
            },
          ],
          name: 'Budgets & Forecasts',
          subRoutes: [],
          url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.BUDGET.INDEX}`,
        },
        {
          flagKey: FeatureFlag.VENDORS,
          name: 'Vendor Contracts',
          subRoutes: [],
          url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.VENDORS}`,
        },
        {
          flagKey: FeatureFlag.PURCHASE_ORDERS,
          name: 'Purchase Orders',
          subRoutes: [],
          url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.PURCHASE_ORDERS}`,
        },
        {
          badge: {
            amount: new BehaviorSubject(0),
            tooltip: '# of Change Orders Pending Approval.',
          },
          flagKeys: ['tab_change_order_review', 'tab_budget_changelog', 'tab_compare'],
          flyOutMenu: [
            {
              flagKey: 'tab_change_order_review',
              name: 'Change Orders',
              subRoutes: [],
              url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}`,
            },
            {
              flagKey: 'tab_budget_changelog',
              name: 'Change Log',
              subRoutes: [],
              url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.CHANGE_LOG}`,
            },
            {
              flagKey: 'tab_compare',
              name: 'Compare',
              subRoutes: [],
              url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.COMPARE}`,
            },
          ],
          name: 'Change Orders',
          subRoutes: [],
          url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}`,
        },
        {
          flagKeys: ['tab_payment_milestones', 'tab_budget_payment_schedule'],
          flyOutMenu: [
            {
              flagKey: 'tab_payment_milestones',
              name: 'Payment Milestones',
              subRoutes: [],
              url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.PAYMENTS.INDEX}/${ROUTING_PATH.BUDGET.PAYMENTS.PAYMENT_MILESTONES}`,
            },
            {
              flagKey: 'tab_budget_payment_schedule',
              name: 'Payment Schedule',
              subRoutes: [],
              url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.PAYMENTS.INDEX}/${ROUTING_PATH.BUDGET.PAYMENTS.PAYMENT_SCHEDULE}`,
            },
          ],
          name: 'Payments',
          subRoutes: [],
          url: `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.PAYMENTS.INDEX}`,
        },
      ],
      svg: 'ZoomMoney',
      url: `/${ROUTING_PATH.BUDGET.INDEX}`,
    },
    {
      flagKey: 'nav_forecast',
      name: 'Forecast Settings',
      subRoutes: [
        {
          flagKey: 'forecast_methodology_tab',
          name: 'Forecast Methodology',
          subRoutes: [],
          url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}`,
        },
        {
          name: 'Trial Timeline',
          subRoutes: [],
          url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.TIMELINE}`,
        },
        {
          flagKeys: ['custom_drivers', 'tab_forecast_drivers'],
          flyOutMenu: [
            {
              flagKey: 'custom_drivers',
              name: 'Custom Drivers',
              subRoutes: [],
              url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.CUSTOM_DRIVERS}`,
            },
            {
              flagKey: 'tab_forecast_drivers',
              name: 'Patient Drivers',
              subRoutes: [
                {
                  flagKey: 'tab_forecast_drivers',
                  hiddenFromNavigation: true,
                  name: 'Patient Curves',
                  subRoutes: [],
                  url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.CURVES}`,
                },
                {
                  flagKey: 'tab_patient_groups',
                  hiddenFromNavigation: true,
                  name: 'Patient Groups',
                  subRoutes: [],
                  url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.GROUPS}`,
                },
              ],
              url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}`,
            },
            {
              flagKey: 'tab_forecast_drivers',
              name: 'Site Drivers',
              subRoutes: [
                {
                  flagKey: 'tab_forecast_drivers',
                  hiddenFromNavigation: true,
                  name: 'Site Curves',
                  subRoutes: [],
                  url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.CURVES}`,
                },
                {
                  flagKey: 'tab_forecast_site_groups',
                  hiddenFromNavigation: true,
                  name: 'Site Groups',
                  subRoutes: [],
                  url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.GROUPS}`,
                },
              ],
              url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.SITE_DRIVER.INDEX}`,
            },
          ],
          name: 'Drivers',
          subRoutes: [],
          url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}`,
        },
      ],
      svg: 'ChartHistogram',
      url: `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}`,
    },
    {
      flagKey: 'nav_investigator',
      flagKeys: [
        'tab_investigator_summary',
        'tab_investigator_transactions',
        'tab_investigator_sites',
        'tab_trial_settings_patient_protocol',
        'nav_patient',
        'tab_protocol_entry',
        'tab_patient_groups',
        'tab_forecast_investigator_detail',
        'tab_forecast_investigator_forecast',
      ],
      name: 'Investigator',
      subRoutes: [
        {
          flagKey: 'tab_investigator_summary',
          name: 'Investigator Summary',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_SUMMARY}`,
        },
        {
          flagKey: 'tab_investigator_transactions',
          name: 'Transactions',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_TRANSACTIONS}`,
        },
        {
          flagKey: 'nav_patient',
          name: 'Patient Tracker',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PATIENT_TRACKER}`,
        },
        {
          flagKeys: ['tab_forecast_investigator_forecast', 'investigator_forecast_settings'],
          flyOutMenu: [
            {
              flagKey: 'tab_forecast_investigator_forecast',
              name: 'Forecast Table',
              subRoutes: [],
              url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.TABLE}`,
            },
            {
              flagKey: 'investigator_forecast_settings',
              name: 'Forecast Settings',
              subRoutes: [],
              url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.SETTINGS}`,
            },
          ],
          name: 'Investigator Forecast',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_FORECAST.INDEX}`,
        },
        {
          flagKeys: ['tab_investigator_sites', 'tab_trial_settings_patient_protocol'],
          flyOutMenu: [
            {
              name: 'Sites',
              subRoutes: [
                {
                  hiddenFromNavigation: true,
                  name: 'Site Details',
                  subRoutes: [],
                  url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/:id/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.DETAILS}`,
                },
                {
                  hiddenFromNavigation: true,
                  name: 'Site Costs',
                  subRoutes: [],
                  url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/:id/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.COSTS}`,
                },
              ],
              url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES}`,
            },
            {
              flagKey: 'tab_trial_settings_patient_protocol',
              name: 'Site Cost Summary',
              subRoutes: [],
              url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITE_COST_SUMMARY}`,
            },
          ],
          name: 'Site Costs',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}`,
        },
        {
          flagKeys: ['tab_protocol_entry', 'tab_patient_groups'],
          flyOutMenu: [
            {
              flagKey: 'tab_protocol_entry',
              name: 'Protocol Entry',
              subRoutes: [],
              url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.INDEX}`,
            },
            {
              flagKey: 'tab_patient_groups',
              name: 'Patient Groups',
              subRoutes: [],
              url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.INDEX}/${ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.PATIENT_GROUPS}`,
            },
          ],
          name: 'Protocol Entry',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.INDEX}`,
        },
        {
          flagKey: 'tab_forecast_investigator_detail',
          name: 'Investigator Detail',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_DETAIL}`,
        },
        {
          flagKey: 'investigator_reconciliation_dashboard',
          name: 'Payments Reconciliation',
          rightSvg: 'Diamond',
          subRoutes: [],
          url: `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PAYMENTS_RECONCILIATION}`,
        },
      ],
      svg: 'CheckupList',
      url: `${ROUTING_PATH.INVESTIGATOR.INDEX}`,
    },
    {
      flagKey: 'nav_home',
      name: 'Trial Dashboard',
      singleRoute: true,
      subRoutes: [],
      url: `/${ROUTING_PATH.DASHBOARD}`,
    },
    {
      flagKey: 'nav_trial_insights',
      flagKeys: ['clin_ops_dashboard', 'finance_dashboard'],
      name: 'Trial Insights',
      subRoutes: [
        {
          flagKey: 'clin_ops_dashboard',
          name: 'ClinOps Dashboard',
          subRoutes: [],
          url: `/${ROUTING_PATH.TRIAL_INSIGHTS.INDEX}/${ROUTING_PATH.TRIAL_INSIGHTS.CLINICAL}`,
        },
        {
          flagKey: 'finance_dashboard',
          name: 'Finance Dashboard',
          subRoutes: [],
          url: `/${ROUTING_PATH.TRIAL_INSIGHTS.INDEX}/${ROUTING_PATH.TRIAL_INSIGHTS.FINANCE}`,
        },
      ],
      svg: 'Timeline',
      url: `/${ROUTING_PATH.TRIAL_INSIGHTS.INDEX}`,
    },
    {
      flagKey: 'tab_compare_scenario_manager',
      name: 'Scenario Manager',
      singleRoute: true,
      subRoutes: [],
      url: `/${ROUTING_PATH.MANAGER}`,
    },
    {
      flagKey: 'nav_risk',
      name: 'Risk Analytics',
      singleRoute: true,
      subRoutes: [],
      url: `/${ROUTING_PATH.RISK_ANALYTICS}`,
    },
    { divider: true, name: '', subRoutes: [], url: '' },
    {
      flagKey: 'nav_document_library',
      name: 'Document Library',
      singleRoute: true,
      subRoutes: [],
      svg: 'Books',
      url: `/${ROUTING_PATH.DOCUMENTS}`,
    },
    {
      flagKey: 'nav_audit_history',
      name: 'Audit History',
      singleRoute: true,
      subRoutes: [],
      svg: 'FileSearch',
      url: `/${ROUTING_PATH.AUDIT_HISTORY}`,
    },
    {
      activeSvg: 'SettingsFilled',
      flyOutMenu: [
        {
          name: 'Users',
          subRoutes: [],
          url: `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.USERS}`,
        },
        {
          flagKey: 'tab_user_permissions',
          name: 'User Permissions',
          subRoutes: [],
          url: `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.USER_PERMISSIONS}`,
        },
        {
          flagKey: 'tab_integrations',
          name: 'Integrations',
          subRoutes: [],
          url: `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.INTEGRATIONS}`,
        },
        {
          flagKey: 'tab_exchange_rates',
          name: 'Exchange Rates',
          subRoutes: [],
          url: `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.EXCHANGE_RATES}`,
        },
        {
          flagKey: 'close_settings',
          flagKeys: [
            'expense_defaults',
            'settings_journal_entry_configuration',
            'balance_sheet_accounts',
          ],
          name: 'Close Settings',
          subRoutes: [
            {
              flagKey: 'expense_defaults',
              hiddenFromNavigation: true,
              name: 'Expense Source Defaults',
              subRoutes: [],
              url: `/settings/close-settings/expense-defaults`,
            },
            {
              flagKey: 'settings_journal_entry_configuration',
              hiddenFromNavigation: true,
              name: 'Journal Entry Configuration',
              subRoutes: [],
              url: `/settings/close-settings/je-configuration`,
            },
            {
              flagKey: 'balance_sheet_accounts',
              hiddenFromNavigation: true,
              name: 'Balance Sheet Accounts',
              subRoutes: [],
              url: `/settings/close-settings/balance-sheet-accounts`,
            },
          ],
          url: `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.CLOSE_SETTINGS.INDEX}`,
        },
        {
          flagKey: 'tab_investigator_settings',
          name: 'Investigator Settings',
          subRoutes: [],
          url: `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.INVESTIGATOR_SETTINGS}`,
        },
        {
          flagKey: 'tab_forecast_studyspecifications',
          name: 'Study Specifications',
          subRoutes: [],
          url: `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.STUDY_SPECIFICATIONS}`,
        },
      ],
      name: 'Settings',
      singleRoute: true,
      subRoutes: [],
      svg: 'Settings',
      url: `/${ROUTING_PATH.SETTINGS.INDEX}`,
    },
    {
      flagKey: 'nav_design_system',
      name: 'Design System',
      singleRoute: true,
      subRoutes: [
        {
          hiddenFromNavigation: true,
          name: 'Colors',
          subRoutes: [],
          url: '/design-system/colors',
        },
        {
          hiddenFromNavigation: true,
          name: 'Iconography',
          subRoutes: [],
          url: '/design-system/iconography',
        },
        {
          hiddenFromNavigation: true,
          name: 'Buttons',
          subRoutes: [],
          url: '/design-system/buttons',
        },
        {
          hiddenFromNavigation: true,
          name: 'Button Toggle Group',
          subRoutes: [],
          url: '/design-system/button-toggle-group-demo',
        },
        {
          hiddenFromNavigation: true,
          name: 'Data Components',
          subRoutes: [],
          url: '/design-system/data-components',
        },
        {
          hiddenFromNavigation: true,
          name: 'Forms & Controls',
          subRoutes: [],
          url: '/design-system/forms-and-controls',
        },
        {
          hiddenFromNavigation: true,
          name: 'Tables',
          subRoutes: [],
          url: '/design-system/tables',
        },
        {
          hiddenFromNavigation: true,
          name: 'Modals & Cards',
          subRoutes: [],
          url: '/design-system/modals',
        },
        {
          hiddenFromNavigation: true,
          name: 'Typography',
          subRoutes: [],
          url: '/design-system/typography',
        },
        {
          hiddenFromNavigation: true,
          name: 'Badge Status',
          subRoutes: [],
          url: '/design-system/badge-status',
        },
      ],
      svg: 'Rocket',
      url: '/design-system',
    },
    {
      activeSvg: 'SettingsFilled',
      flagKey: 'nav_ops_admin',
      name: 'Ops Admin',
      singleRoute: true,
      subRoutes: [
        {
          hiddenFromNavigation: true,
          name: 'EDC Converter',
          subRoutes: [
            {
              hiddenFromNavigation: true,
              name: 'Legacy',
              subRoutes: [],
              url: `/ops-admin/edc-converter/legacy`,
            },
            {
              hiddenFromNavigation: true,
              name: 'New',
              subRoutes: [],
              url: `/ops-admin/edc-converter/new`,
            },
          ],
          url: `/ops-admin/edc-converter`,
        },
        {
          hiddenFromNavigation: true,
          name: 'JE Generator',
          subRoutes: [],
          url: `/ops-admin/je-generator`,
        },
        {
          hiddenFromNavigation: true,
          name: 'Extractor',
          subRoutes: [],
          url: `/ops-admin/extractor`,
        },
        {
          hiddenFromNavigation: true,
          name: 'Site Payments: Invoice Generator',
          subRoutes: [],
          url: `/ops-admin/invoice-generator`,
        },
      ],
      svg: 'Settings',
      url: '/ops-admin',
    },
    {
      activeSvg: 'ClipboardCheck',
      flagKey: 'nav_qa_testing',
      name: 'QA Testing',
      singleRoute: true,
      subRoutes: [
        {
          hiddenFromNavigation: true,
          name: 'Email',
          subRoutes: [],
          url: `/${ROUTING_PATH.QA_TESTING.INDEX}/${ROUTING_PATH.QA_TESTING.EMAIL}`,
        },
      ],
      svg: 'ClipboardCheck',
      url: '/qa-testing',
    },
    // Account pages (typically accessed via profile dropdown but useful for search)
    {
      hiddenFromNavigation: true,
      name: 'Account',
      subRoutes: [
        {
          hiddenFromNavigation: true,
          name: 'Profile & Settings',
          subRoutes: [],
          url: `/${ROUTING_PATH.ACCOUNT.INDEX}/${ROUTING_PATH.ACCOUNT.SETTINGS}`,
        },
        {
          hiddenFromNavigation: true,
          name: 'Notification Preferences',
          subRoutes: [],
          url: `/${ROUTING_PATH.ACCOUNT.INDEX}/${ROUTING_PATH.ACCOUNT.NOTIFICATIONS}`,
        },
      ],
      url: `/${ROUTING_PATH.ACCOUNT.INDEX}`,
    },
  ];
}
